import type { OnboardingOnboardingAnswers } from '@customerio/hydra-ui-api';
import { isEmptyString } from '@/utils/string';

/**
 * The onboarding wizard's question wording and user-facing answer labels,
 * shared so recaps of the answers (like the prefilled workflow prompt) quote
 * the wizard verbatim and track its copy.
 */
export const ONBOARDING_QUESTIONS = {
  goal: "What's one thing you want to achieve with Customer.io?",
  startingPoint: 'What do you want to build first?',
  platform: 'Where do your people use your product: on the web, in a mobile app, or both?',
  channels: 'Which channels do you plan to use?',
} as const;

/** Example goals offered as chips, shaped like a goal the user would state, not a category. */
export const GOAL_LABELS = [
  'Re-engage customers who stopped logging in',
  'Convert more trial signups to paid',
  'Get new users to their first key action faster',
  'Recover failed payments before churn',
] as const;

export type GoalLabel = (typeof GOAL_LABELS)[number];

const GOAL_GERUNDS = new Map<string, string>(
  Object.entries({
    'Re-engage customers who stopped logging in': 're-engaging customers who stopped logging in',
    'Convert more trial signups to paid': 'converting more trial signups to paid',
    'Get new users to their first key action faster': 'getting new users to their first key action faster',
    'Recover failed payments before churn': 'recovering failed payments before churn',
  } satisfies Record<GoalLabel, string>),
);

/**
 * The goal as a gerund phrase ("re-engaging customers who stopped logging in")
 * for copy that reads it back mid-sentence, or undefined for a typed or legacy
 * goal, which has no safe inflection.
 */
export function goalGerund(goal: string | null | undefined): string | undefined {
  return goal == null ? undefined : GOAL_GERUNDS.get(goal);
}

export const STARTING_POINT_LABELS = {
  transactional: 'Transactional messages',
  broadcasts: 'Broadcasts',
  workflows: 'Workflows',
  user_segmentation: 'Segments',
} as const satisfies Record<NonNullable<OnboardingOnboardingAnswers['starting_point']>, string>;

/**
 * One wire value of the persisted `answers.channels` array, derived from the
 * generated model so the type can never drift from the spec.
 */
export type Channel = NonNullable<OnboardingOnboardingAnswers['channels']>[number];

/**
 * A label for every channel the server accepts. Unlike the platform question,
 * these are wire values directly: the answer is the set the user picked, so
 * there is no choice-to-wire mapping to own. Channels the wizard no longer
 * offers stay labelled so a stored answer still replays.
 */
export const CHANNEL_LABELS = {
  email: 'Email',
  sms: 'SMS',
  push: 'Push notifications',
  in_app: 'In-app',
  whatsapp: 'WhatsApp',
  slack: 'Slack',
  webhooks: 'Webhooks',
} as const satisfies Record<Channel, string>;

/**
 * Canonical order for every accepted channel: the stored set is written and
 * read back in this order. Every `Channel` is labelled — `CHANNEL_LABELS` is
 * checked against the full union, and `channelsAnswerLabel` indexes it by
 * `Channel` — so a value missing from the spec cannot slip in here.
 */
export const CHANNEL_ORDER: readonly Channel[] = ['email', 'sms', 'push', 'in_app', 'whatsapp', 'slack', 'webhooks'];

/** The channels the wizard offers as chips, in display order. */
export const WIZARD_CHANNELS = ['email', 'sms', 'push', 'in_app', 'webhooks'] as const satisfies readonly Channel[];

export type WizardChannel = (typeof WIZARD_CHANNELS)[number];

/**
 * The user-facing label the channels answer displays as, or null when
 * unanswered. Ordered by `CHANNEL_ORDER` rather than by the stored array, so
 * the same set always reads back the same way.
 */
export function channelsAnswerLabel(channels: OnboardingOnboardingAnswers['channels']): string | null {
  const picked = new Set(channels ?? []);
  const labels = CHANNEL_ORDER.filter((channel) => picked.has(channel)).map((channel) => CHANNEL_LABELS[channel]);
  return labels.length === 0 ? null : labels.join(', ');
}

export const PLATFORM_LABELS = {
  mobile: 'Mobile',
  web: 'Web',
  both: 'Both',
} as const;

/** The user-facing label the platform answer displays as, or null when unanswered. */
export function platformAnswerLabel(listenTargets: OnboardingOnboardingAnswers['listen_targets']): string | null {
  const targets = new Set(listenTargets ?? []);
  const hasWebsite = targets.has('website');
  const hasMobileApp = targets.has('mobile_app');
  if (hasWebsite && hasMobileApp) return PLATFORM_LABELS.both;
  if (hasMobileApp) return PLATFORM_LABELS.mobile;
  return hasWebsite ? PLATFORM_LABELS.web : null;
}

/** The answered wizard questions in asked order, quoting the wizard's wording. */
export function recapAnswers(answers: OnboardingOnboardingAnswers): Array<{ question: string; answer: string }> {
  const recap: Array<{ question: string; answer: string }> = [];
  const goal = answers.goal;
  if (goal != null && !isEmptyString(goal)) {
    recap.push({ question: ONBOARDING_QUESTIONS.goal, answer: goal });
  }
  if (answers.starting_point != null) {
    recap.push({
      question: ONBOARDING_QUESTIONS.startingPoint,
      answer: STARTING_POINT_LABELS[answers.starting_point],
    });
  }
  const platform = platformAnswerLabel(answers.listen_targets);
  if (platform != null) {
    recap.push({ question: ONBOARDING_QUESTIONS.platform, answer: platform });
  }
  const channels = channelsAnswerLabel(answers.channels);
  if (channels != null) {
    recap.push({ question: ONBOARDING_QUESTIONS.channels, answer: channels });
  }
  return recap;
}
